// Builds one frame of a timeline (v6) episode: the picture at t, then the talking face of every character on screen,
// all seen through the camera at t. Pure, like shotFrame, so preview and export always match.
import { faceSpritesInUse, faceStyleOf } from "../library/faceSets";
import type { Camera } from "../model/episode";
import type { Cue, Viseme } from "../model/face";
import { cameraAt, faceAt, feelingAt, lineAt, marksOf, motionOf, onScreen, shownAt, type Framing, type TimelineEpisode } from "../model/timeline";
import { FRAME_H, FRAME_W, WHOLE, pictureFit, viewRect } from "./camera";
import { BASE, faceImages, type DrawFrame, type DrawImage } from "./frame";
import { blinkingAt } from "./face";
import { hash } from "./rng";
import { FPS } from "./timeline";
import { visemeFrames } from "./visemes";

// Keyed by the cues array, which stays the same object while the line is untouched (a resolved line is a fresh copy each time).
const visemeCache = new WeakMap<Cue[], Viseme[]>();
function visemesOf(line: { status: string; cues: Cue[]; duration: number }): Viseme[] {
  if (line.status !== "ready") return [];
  let v = visemeCache.get(line.cues);
  if (!v) visemeCache.set(line.cues, (v = visemeFrames(line.cues, line.duration, FPS)));
  return v;
}

// The mouth shape a character shows at t: the viseme of their line covering t, or undefined for rest.
export function mouthAt(ep: TimelineEpisode, char: string, t: number): Viseme | undefined {
  const line = lineAt(ep, char, t);
  return line ? visemesOf(line)[Math.floor((t - line.start) * FPS)] : undefined;
}

// `clock`: seconds of wall time (blinks and the boil keep going while the playhead is held).
export function timelineFrameAt(ep: TimelineEpisode, t: number, clock: number): DrawFrame {
  const frame = Math.floor(clock * FPS);
  const view = viewRect(cameraAt(ep, t).camera);
  const shown = shownAt(ep, t);
  if (!shown) return { frame, view, base: BASE, boil: null, images: [] };
  const boil = ep.wiggle > 0 && shown.mark.boil !== false ? { strength: ep.wiggle, every: 3 } : null;
  const pic = shown.picture;
  const fit = pictureFit(pic);
  const images: DrawImage[] = [{ href: pic.src, x: fit.x, y: fit.y, w: pic.w * fit.s, h: pic.h * fit.s }];
  for (const char of onScreen(ep, t)) {
    const mark = faceAt(ep, char, t)!;
    const blink = blinkingAt(hash(mark.id), char, clock);
    images.push(...faceImages(mark.face, fit, feelingAt(ep, char, t), blink, mouthAt(ep, char, t), faceStyleOf(char)));
  }
  return { frame, view, base: BASE, boil, images };
}

// Every image the episode can show between two times: the pictures held then, and the face sprites.
export function imagesInRange(ep: TimelineEpisode, from: number, to: number): string[] {
  const out = faceSpritesInUse();
  const pics = marksOf(ep, "picture");
  pics.forEach((p, i) => {
    const end = i === pics.length - 1 ? Infinity : p.end; // the last picture holds to the end
    if (end > from && p.t < to) out.push(p.picture.src, ...(motionOf(p)?.frames.map(f => f.picture.src) ?? []));
  });
  return out;
}

// The camera for a framing, from the face of `on` at time t (mirrors "mid" and "close" in cameraChoices, engine/frame.ts).
// Falls back to the whole picture when there is no character, face or picture to point at.
export function resolveFraming(ep: TimelineEpisode, t: number, framing: Framing, on?: string): Camera {
  if (framing === "whole" || framing === "free" || !on) return WHOLE; // a free framing keeps its own camera
  const mark = faceAt(ep, on, t);
  const shown = shownAt(ep, t);
  if (!mark || !shown) return WHOLE;
  const fit = pictureFit(shown.picture), face = mark.face;
  const c = { x: fit.x + ((face.eyes.x + face.mouth.x) / 2) * fit.s, y: fit.y + ((face.eyes.y + face.mouth.y) / 2) * fit.s };
  // Zoom from how big the face is: a close-up makes the face about a third of the frame's width.
  const fw = face.eyes.w * fit.s * 1.8;
  const close = Math.min(6, Math.max(1.4, FRAME_W / 3 / fw));
  const mid = Math.max(1.2, close * 0.55);
  const zoom = framing === "close" ? close : mid;
  // The face sits a little above the middle of the frame.
  return { cx: c.x / FRAME_W, cy: (c.y + (FRAME_H / zoom) * 0.12) / FRAME_H, zoom };
}
